/**
 * The signature-validation report, as the workflow's End node emits it.
 *
 * Parsed defensively rather than cast: this comes from an LLM's structured output passed through a
 * code node, and a demo that throws a white screen because one field arrived as a string is worse
 * than one that renders what it has.
 *
 * ## ⚠️ Three rules from the workflow's design that the UI must not soften
 *
 * **1. `document_safe` is FAIL-CLOSED.** It is false when there is any `fail` **or** any
 * `needs_review`. "The model was not sure" must never round up to a pass. For a product whose
 * output is *"this contract is properly executed"*, every failure mode has to point at unsafe.
 *
 * **2. `not_applicable` is NEUTRAL, not a warning.** Under a blocks-only rule, a body page requires
 * nothing — it is neither safe nor unsafe. A boolean design called those pages unsafe and produced
 * seventeen false alarms on a twenty-page contract. Rendering them as amber would reintroduce the
 * bug the four-state design exists to fix.
 *
 * **3. Matching is a vision model's similarity judgement, not handwriting analysis.** The
 * confidence score is a triage aid — "look at this page first" — and is not forensic evidence. No
 * label in this app may imply otherwise.
 */

export type PageStatus = 'pass' | 'fail' | 'not_applicable' | 'needs_review'
export type PageKind = 'signature_block' | 'body' | 'unclear'

export interface PartyResult {
  name: string
  /** `matched` | `present_unverified` | `missing` | anything the model returns. */
  status: string
  /** 0–100. ⚠️ Triage, not evidence — see the module note. */
  confidence: number
}

export interface PageObservation {
  page_number: number
  page_kind: PageKind
  status: PageStatus
  expected_marks: string
  marks_found: number
  party_results: PartyResult[]
  reasoning: string
}

export interface Report {
  document_safe: boolean
  signing_rule: string
  /** ⚠️ Distinguishes "no samples were supplied" from "matched, with low confidence". */
  match_performed: boolean
  parties: string[]
  pages_analyzed: number
  /** ⚠️ Beside `pages_analyzed`, so a silent drop is visible rather than reported confidently. */
  pages_expected: number
  pages_flagged: number[]
  pages_needing_review: number[]
  page_observations: PageObservation[]
}

const PAGE_STATUSES: PageStatus[] = ['pass', 'fail', 'not_applicable', 'needs_review']
const PAGE_KINDS: PageKind[] = ['signature_block', 'body', 'unclear']

const rec = (v: unknown): Record<string, unknown> => (typeof v === 'object' && v !== null ? (v as Record<string, unknown>) : {})
const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : [])
const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0)
const str = (v: unknown): string => (typeof v === 'string' ? v : '')

/**
 * ⚠️ An unrecognised page status becomes `needs_review`, never `pass`.
 *
 * Same fail-closed direction as `document_safe`. If the model or a future version of the workflow
 * returns something this build has not heard of, the honest rendering is "somebody should look",
 * not a green tick.
 */
function toPageStatus(v: unknown): PageStatus {
  const s = str(v)
  return (PAGE_STATUSES as string[]).includes(s) ? (s as PageStatus) : 'needs_review'
}

function toPageKind(v: unknown): PageKind {
  const s = str(v)
  return (PAGE_KINDS as string[]).includes(s) ? (s as PageKind) : 'unclear'
}

/**
 * Pull the report out of a run's outputs.
 *
 * The End node declares one output, `AI_Observations`. ⚠️ It is a real nested object — it was
 * double-encoded at one point (a JSON string inside the JSON envelope) and fixed by having the
 * aggregator return `SegmentType.OBJECT`. A string is still accepted here, because a workflow
 * edited later could reintroduce it and a demo should degrade rather than break.
 */
export function reportFrom(outputs: Record<string, unknown> | null): Report | null {
  if (outputs === null) return null

  let raw = outputs['AI_Observations'] ?? outputs['ai_observations']
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw)
    } catch {
      return null
    }
  }
  const r = rec(raw)
  if (Object.keys(r).length === 0) return null

  const observations = arr(r['page_observations']).map((o): PageObservation => {
    const p = rec(o)
    return {
      page_number: num(p['page_number']),
      page_kind: toPageKind(p['page_kind']),
      status: toPageStatus(p['status']),
      expected_marks: str(p['expected_marks']),
      marks_found: num(p['marks_found']),
      party_results: arr(p['party_results']).map((x): PartyResult => {
        const q = rec(x)
        return { name: str(q['name']), status: str(q['status']), confidence: num(q['confidence']) }
      }),
      reasoning: str(p['reasoning']),
    }
  })

  return {
    /*
     * ⚠️ RECOMPUTED, not trusted.
     *
     * The workflow's aggregator already fails closed, and this agrees with it — but the rule is the
     * single most important line in the design and the UI is the last place it can be enforced. A
     * `document_safe: true` arriving beside a page marked `fail` is a bug somewhere upstream, and
     * the safe reading of a contradiction is unsafe.
     */
    document_safe:
      r['document_safe'] === true && !observations.some((o) => o.status === 'fail' || o.status === 'needs_review'),
    signing_rule: str(r['signing_rule']),
    match_performed: r['match_performed'] === true,
    parties: arr(r['parties']).map(str).filter(Boolean),
    pages_analyzed: num(r['pages_analyzed']),
    pages_expected: num(r['pages_expected']),
    pages_flagged: arr(r['pages_flagged']).map(num),
    pages_needing_review: arr(r['pages_needing_review']).map(num),
    page_observations: observations,
  }
}

/** The four signing rules, in the words a client uses rather than the wire value. */
export const SIGNING_RULES: { value: string; title: string; detail: string }[] = [
  {
    value: 'full_every_page',
    title: 'Full signature on every page',
    detail: 'Every party signs in full, on every page of the document.',
  },
  {
    value: 'initials_every_page',
    title: 'Initials on every page',
    detail: 'Every party initials every page.',
  },
  {
    value: 'full_on_blocks',
    title: 'Full signatures at the signature blocks',
    detail: 'Only pages carrying a signature block need marks. Body pages require nothing.',
  },
  {
    value: 'initials_and_full_on_blocks',
    title: 'Initials everywhere, full signatures at the blocks',
    detail: 'Initials from each party on every page, plus full signatures wherever there is a block.',
  },
]

export const ruleTitle = (value: string): string =>
  SIGNING_RULES.find((r) => r.value === value)?.title ?? value

/** How a page's four states read to somebody who has not read the design document. */
export const STATUS_COPY: Record<PageStatus, { label: string; detail: string }> = {
  pass: { label: 'Signed', detail: 'The expected marks are present.' },
  fail: { label: 'Problem', detail: 'Expected marks are missing or did not match.' },
  // ⚠️ Neutral wording on purpose. "Nothing required" is a fact about the rule, not a finding.
  not_applicable: { label: 'Nothing required', detail: 'The signing rule asks nothing of this page.' },
  needs_review: { label: 'Needs review', detail: 'Unclear — a person should look at this page.' },
}
